import { useEffect, useState, useMemo, useRef } from 'react';
import { useWidget } from '../WidgetProvider';
import { Button } from '@/components/ui/button';
import { Loader2, Sparkles, ImageIcon, AlertCircle, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ReviewsCarousel, TrustBadges, BeforeAfterGallery } from '../social-proof';

export function PreviewTeaserStep() {
  const {
    state,
    generationStatus,
    generationError,
    uploadImagesToS3,
    goToStep,
    tenantConfig,
    trackEvent,
  } = useWidget();

  const hasStartedRef = useRef(false);
  const [uploadComplete, setUploadComplete] = useState(false);

  // Create preview URLs from local File objects
  const previewUrls = useMemo(() => {
    return state.imageFiles.map((file) => URL.createObjectURL(file));
  }, [state.imageFiles]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  // Start S3 upload silently on mount
  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    trackEvent('preview_teaser_viewed');

    // If images already uploaded (session resume), skip upload
    if (state.uploadedImages.length > 0) {
      setUploadComplete(true);
      return;
    }

    // If no image files (session resume without files), redirect to upload
    if (state.imageFiles.length === 0) {
      goToStep('image_upload');
      return;
    }

    const doUpload = async () => {
      await uploadImagesToS3();
      setUploadComplete(true);
    };
    doUpload();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track when upload completes via status change
  useEffect(() => {
    if (generationStatus === 'idle' && state.uploadedImages.length > 0) {
      setUploadComplete(true);
    }
  }, [generationStatus, state.uploadedImages]);

  const isUploading = generationStatus === 'uploading';
  const hasError = generationStatus === 'error' && !uploadComplete;

  const handleContinue = () => {
    trackEvent('preview_teaser_continued');
    goToStep('selections');
  };

  const handleRetryUpload = () => {
    hasStartedRef.current = false;
    setUploadComplete(false);
    uploadImagesToS3().then(() => setUploadComplete(true));
  };

  // Use preview URLs from local files, or fall back to uploaded S3 URLs
  const displayImages = previewUrls.length > 0 ? previewUrls : state.uploadedImages;

  return (
    <div className="space-y-6">
      {/* Original Images with Overlay */}
      <div className="relative rounded-xl overflow-hidden shadow-lg">
        {displayImages.length > 0 ? (
          <div className={cn(
            'grid gap-1',
            displayImages.length === 1 && 'grid-cols-1',
            displayImages.length === 2 && 'grid-cols-2',
            displayImages.length >= 3 && 'grid-cols-2',
          )}>
            {displayImages.map((url, i) => (
              <div
                key={i}
                className={cn(
                  'relative overflow-hidden',
                  displayImages.length === 1 && 'aspect-[4/3]',
                  displayImages.length === 2 && 'aspect-square',
                  displayImages.length >= 3 && i === 0 && 'aspect-square col-span-2',
                  displayImages.length >= 3 && i > 0 && 'aspect-[4/3]',
                )}
              >
                <img
                  src={url}
                  alt={`Your room photo ${i + 1}`}
                  className="w-full h-full object-cover brightness-[0.6]"
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="w-full aspect-[4/3] bg-muted flex items-center justify-center">
            <ImageIcon className="w-12 h-12 text-muted-foreground" />
          </div>
        )}

        {/* CTA Overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-6 text-center">
          <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-bold drop-shadow-lg">
            Your AI design is almost ready
          </h2>
          <p className="text-sm text-white/90 mt-2 max-w-xs drop-shadow-md">
            Complete a few more steps to unlock your personalized renovation preview
          </p>
        </div>
      </div>

      {/* Upload Status - subtle indicator */}
      {isUploading && (
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Preparing your photos...</span>
        </div>
      )}

      {uploadComplete && !hasError && (
        <div className="flex items-center justify-center gap-2 text-sm text-green-600 dark:text-green-500">
          <CheckCircle2 className="h-4 w-4" />
          <span>Photos ready</span>
        </div>
      )}

      {/* Error State */}
      {hasError && (
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span>{generationError || 'Upload failed'}</span>
          </div>
          <Button variant="outline" size="sm" onClick={handleRetryUpload}>
            Retry Upload
          </Button>
        </div>
      )}

      {/* Continue Button */}
      <Button
        className="w-full h-12 text-base font-semibold bg-accent hover:bg-accent/90 text-accent-foreground shadow-md shadow-accent/20 transition-all hover:shadow-lg hover:shadow-accent/25 active:scale-[0.98] disabled:opacity-50 disabled:shadow-none"
        size="lg"
        onClick={handleContinue}
        disabled={!uploadComplete || hasError}
      >
        {isUploading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Uploading photos...
          </>
        ) : (
          'Continue to Unlock Your Design'
        )}
      </Button>

      {/* Social Proof Section */}
      {tenantConfig?.widget && (
        <div className="w-full space-y-6 mt-4">
          {tenantConfig.widget.reviews && tenantConfig.widget.reviews.length > 0 && (
            <ReviewsCarousel reviews={tenantConfig.widget.reviews} />
          )}

          {tenantConfig.widget.trust_badges &&
            tenantConfig.widget.trust_badges.filter((b) => b.enabled).length > 0 && (
              <TrustBadges badges={tenantConfig.widget.trust_badges.filter((b) => b.enabled)} />
            )}

          {tenantConfig.widget.before_after_projects &&
            tenantConfig.widget.before_after_projects.length > 0 && (
              <BeforeAfterGallery projects={tenantConfig.widget.before_after_projects} />
            )}
        </div>
      )}

      {/* Trust signal */}
      <p className="text-xs text-center text-muted-foreground">
        Your photos are securely uploaded and only used for your renovation design.
      </p>
    </div>
  );
}
